import { useEffect, useRef } from "react";

import type { IDAChatMessage } from "../utils/chatMessageMapper";
import {
  collectCompletedHeartbeatExecutions,
  emitHeartbeatsChanged,
} from "../utils/heartbeatEvents";

export function useHeartbeatCompletionSignal(
  messages: IDAChatMessage[],
  conversationId: string | number | null | undefined,
): void {
  const seededRef = useRef(false);
  const completedExecutionKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    seededRef.current = false;
    completedExecutionKeysRef.current = new Set();
  }, [conversationId]);

  useEffect(() => {
    const completedExecutions = collectCompletedHeartbeatExecutions(messages);
    const nextKeys = new Set(
      completedExecutions.map((execution) => execution.key),
    );

    if (!seededRef.current) {
      completedExecutionKeysRef.current = nextKeys;
      seededRef.current = true;
      return;
    }

    for (const execution of completedExecutions) {
      if (completedExecutionKeysRef.current.has(execution.key)) continue;
      emitHeartbeatsChanged(execution.detail);
      // Keep the active conversation's seen set monotonic. A stale response can
      // omit a completion that a newer response already contained; replacing the
      // set with that stale snapshot would emit the same completion again when it
      // reappears.
      completedExecutionKeysRef.current.add(execution.key);
    }
  }, [messages]);
}
